import React from "react";
import Head from "next/head";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { IoIosSearch } from "react-icons/io";
import { PiSignature } from "react-icons/pi";
import { MdDateRange } from "react-icons/md";
import { FaReact, FaGithub } from "react-icons/fa";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import { MdLibraryBooks } from "react-icons/md";
import { a11yDark } from "../../../utils/react-syntax-highlighter";

import { ButtonPrimary, ButtonSecondary } from "@components/Buttons";
import SampleEmbeddedForm from "@components/Embedded/SampleEmbeddedForm";
import Subscribe from "@components/Subscribe";
import ApiAddonPricingCard from "@components/Pricing/ApiAddonPricingCard";
import {
  CLMButtonPrimary,
  CLMButtonSecondaryBorder,
} from "components/CLM/v3/Buttons";
import { HiArrowRight } from "react-icons/hi";
import ApiAddonPricingCard2 from "@components/Pricing/ApiAddonPricingCard2";

const Embedding = () => {
  return (
    <>
      <Head>
        <title>BoloSign - AI for Smart Contract Management | Embedding</title>
        <meta
          name="description"
          content="Embed BoloSign's document signing process into any web or mobile app."
        />
        <meta
          name="keywords"
          content="bolosign embedding, signature embedding, esignature embedding, bolosign esignature embedding, boloforms esignature embedding,esignature, electronic signature, document signing, document builder, forms, templates, boloforms, bolosign, embedding"
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign " />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta
          property="og:url"
          content="https://bolosign.com/signature/embedding"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management | Embedding"
        />
        <meta
          property="og:description"
          content="Embed BoloSign's document signing process into any web or mobile app."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta
          property="twitter:url"
          content="https://bolosign.com/signature/embedding"
        />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management | Embedding"
        />
        <meta
          name="twitter:description"
          content="Embed BoloSign's document signing process into any web or mobile app."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link rel="canonical" href="https://www.bolosign.com/signature/embedding" />
      </Head>

      <div className="md:mt-20 mt-8 max-w-screen-xl mx-auto px-4 lg:px-8 mb-8">
        <div className="w-full">
          <section className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8">
            <div className="flex justify-center">
              <div className="max-w-3xl">
                <h1 className="text-left text-4xl lg:text-5xl lg:leading-tight font-extrabold text-gray-900 mb-4">
                  <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                    Embedded
                  </span>
                  <br />
                  Document Signing
                </h1>

                <p className="text-left text-xl lg:leading-8 text-gray-700">
                  Use our <b>no-code</b> solution to effortlessly integrate the
                  document signing process into any web or mobile app.
                </p>

                <div className="flex justify-center lg:justify-start">
                  <div className="mt-8 flex items-center gap-3 flex-wrap">
                    <CLMButtonPrimary
                      buttonText={"Try now for free"}
                      buttonUrl={
                        "https://signature.boloforms.com/settings?type=DEV_SEC&subType=EMBED&from=EMBEDDING_PAGE"
                      }
                    />
                    <CLMButtonSecondaryBorder
                      buttonText="Documentation"
                      buttonUrl="https://developer.boloforms.com/"
                      icon={<MdLibraryBooks />}
                    />
                  </div>
                </div>
              </div>
            </div>

            <a
              href="#sample"
              className="border bg-secondary rounded-2xl shadow-md overflow-hidden"
            >
              <div className="flex gap-4 items-center mx-4 my-3 py-1">
                <div className="flex gap-1">
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                </div>

                <div className="grow px-2 bg-white rounded-full flex items-center gap-2">
                  <IoIosSearch className="text-gray-500 text-xl" />
                  <div className="py-1">https://yourwebsite.com</div>
                </div>
              </div>

              <div className="p-4 bg-white">
                <div className="border p-4 rounded-lg relative">
                  <div className="mb-2.5 font-bold text-lg text-center">
                    Your Document
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 hidden lg:block"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 hidden lg:block"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 hidden lg:block"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5 hidden lg:block"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5"></div>
                  <div className="h-3 bg-gray-200 rounded-full mb-2.5"></div>
                  <div className="grid grid-cols-2 gap-12">
                    <div className="h-3 bg-gray-200 rounded-full"></div>
                    <div className="h-3 bg-gray-200 rounded-full"></div>
                  </div>

                  <div className="flex justify-between space-x-4 my-4">
                    <div className="border border-red-400 rounded-lg opacity-50 flex items-center justify-center h-full w-full bg-red-100">
                      <span className="flex justify-center items-center space-x-1 h-full">
                        <MdDateRange className="text-4xl" />
                      </span>
                    </div>
                    <div className="border border-red-400 rounded-lg opacity-50 flex items-center justify-center h-full w-full bg-red-100">
                      <span className="flex justify-center items-center space-x-1 h-full">
                        <PiSignature className="text-4xl" />
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <button className="bg-primary block text-center px-4 py-1 text-white border rounded-full">
                      Submit Form
                    </button>
                  </div>
                </div>
              </div>
            </a>
          </section>

          <section className="py-20 md:py-28">
            <h1 className="max-w-5xl mx-auto text-3xl sm:text-5xl font-bold text-center mb-10 sm:mb-16">
              Pricing and Features
            </h1>
            <div className="max-w-screen-xl mx-auto">
              <ApiAddonPricingCard2
                currentPage={"embedding"}
                title={"Embedding"}
                subTitle={
                  "Streamline your signature workflow by embedding our solution directly into your systems."
                }
              />
            </div>
          </section>

          <section className="w-full sm:mt-8">
            <div className="grid grid-cols-1 gap-8 sm:gap-16 lg:grid-cols-2 items-center">
              <div className="">
                <h2 className="text-3xl lg:text-6xl  text-gray-900 font-bold mb-4">
                  Integrate signing forms in a snap
                </h2>
                <div className="">
                  <p className="text-lg md:text-2xl text-gray-700 ">
                    Simply embed this code into any section of your website to
                    seamlessly introduce the document signing process into your
                    applications. It&apos;s an ideal solution tailored for CRM,
                    KYC, and onboarding workflows, ensuring a streamlined
                    experience for your users.
                  </p>
                </div>
              </div>

              <div className="my-auto overflow-hidden" id="embed-section">
                <div className="mx-auto my-auto sm:mx-0 sm:max-w-none">
                  <div className="bg-black text-white rounded-2xl">
                    <div className="w-full flex items-center justify-between px-4 py-2">
                      <div className="flex items-center gap-2">
                        {Array.from({ length: 3 }, (_, index) => (
                          <div
                            key={index}
                            className="w-2 h-2 rounded-full bg-gray-200"
                          ></div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 text-white cursor-pointer">
                        <span className="">
                          <label>
                            <input type="radio" className="peer hidden" />
                            <span
                              className="peer-checked:hidden flex items-center space-x-2"
                              onClick={() =>
                                navigator.clipboard.writeText(
                                  `<script src="https://appscript-scripts.s3.ap-south-1.amazonaws.com/boloforms-embed-index.min.js"></script>

<script>
	// Create an instance of BoloFormsEmbed
	let embed = new BoloFormsEmbed({
		containerId: "embed-container",
		token: "your-embed-key",
		iframeOpts: { width: "100%", height: "600px" },
		sessionInfo: { userId: 1, userName: "John Doe" },
	});
</script>`
                                )
                              }
                            >
                              <LuCopy />

                              <span className="hidden md:inline">Copy</span>
                            </span>

                            <span className="hidden peer-checked:flex items-center space-x-2">
                              <LuCopyCheck />

                              <span className="hidden md:inline">Copied</span>
                            </span>
                          </label>
                        </span>
                      </div>
                    </div>

                    <div className="w-full pt-0 p-2">
                      <SyntaxHighlighter language="javascript" style={a11yDark}>
                        {`<script src="https://appscript-scripts.s3.ap-south-1.amazonaws.com/boloforms-embed-index.min.js"></script>

<script>
	// Create an instance of BoloFormsEmbed
	let embed = new BoloFormsEmbed({
		containerId: "embed-container",
		token: "your-embed-key",
		iframeOpts: { width: "100%", height: "600px" },
		sessionInfo: { userId: 1, userName: "John Doe" },
	});
</script>`}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="sample" className="pt-16 sm:pt-28">
            <h2 className="max-w-5xl mx-auto text-3xl sm:text-5xl font-bold text-center mb-10 sm:mb-16">
              Live Demo
            </h2>

            <div
              href="#sample"
              className="border bg-secondary rounded-2xl shadow-md overflow-hidden"
            >
              <div className="flex gap-4 items-center mx-4 my-3 py-1">
                <div className="flex gap-1">
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                  <div className="bg-gray-500 rounded-full w-3 h-3"></div>
                </div>

                <div className="grow px-2 bg-white rounded-full flex items-center gap-2">
                  <IoIosSearch className="text-gray-500 text-xl" />
                  <div className="py-1">https://yourwebsite.com</div>
                </div>
              </div>

              <div className="p-4 bg-white">
                <div className="border p-4 rounded-lg relative">
                  <SampleEmbeddedForm />
                </div>
              </div>
            </div>
          </section>

          <section className="w-full mt-16 sm:mt-32">
            <div className="w-full flex flex-col gap-8 sm:gap-10 justify-center items-center">
              <div className="mx-auto lg:mx-0 lg:max-w-screen-md text-center">
                <h2 className="mx-auto text-3xl sm:text-5xl font-bold text-center mb-4 flex justify-center items-center gap-4">
                  <FaReact className="text-[#61dafb] text-4xl sm:text-5xl" />
                  <span>For React Apps</span>
                </h2>

                <p className="my-4 text-lg sm:text-xl text-gray-700 tracking-tight">
                  Effortlessly incorporate BoloSign&apos;s React components into
                  your application, providing you with the flexibility to tailor
                  them precisely to your requirements, ensuring a cohesive user
                  experience.
                </p>

                {/* <div className="flex justify-center items-center gap-3 flex-wrap">
                  <ButtonPrimary
                    buttonText={"Contact Us"}
                    buttonUrl={"https://cal.com/boloforms/signature"}
                    borderShadow={false}
                  />
                  <ButtonSecondary
                    buttonText="Documentation"
                    buttonUrl="https://developer.boloforms.com/"
                    icon={<MdLibraryBooks />}
                    borderShadow={false}
                    bgColor="bg-transparent"
                  />
                </div> */}
              </div>

              <div className="w-full flex justify-center items-center">
                <div className="w-full max-w-screen-lg">
                  <div className="bg-black text-white rounded-2xl">
                    <div className="w-full flex items-center justify-between px-4 py-2">
                      <div className="flex items-center gap-2">
                        {Array.from({ length: 3 }, (_, index) => (
                          <div
                            key={index}
                            className="w-2 h-2 rounded-full bg-gray-200"
                          ></div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 text-white cursor-pointer">
                        <span className="">
                          <label>
                            <input type="radio" className="peer hidden" />
                            <span
                              className="peer-checked:hidden flex items-center space-x-2"
                              onClick={() =>
                                navigator.clipboard.writeText(
                                  `import React, { useEffect } from "react";

const SampleEmbeddedForm = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://appscript-scripts.s3.ap-south-1.amazonaws.com/boloforms-embed-index.min.js";
    script.async = true;
    script.onload = () => {
      if (document.querySelector("#embed-container").childElementCount > 0)
        return;

      // Create an instance of BoloFormsEmbed once the script is loaded
      const embed = new BoloFormsEmbed({
        containerId: "embed-container",
        token: "your-embed-key",
        iframeOpts: { width: "100%", height: "600px" },
        sessionInfo: { userId: 1, userName: "John Doe" },
      });
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return <div id="embed-container" />;
};`
                                )
                              }
                            >
                              <LuCopy />

                              <span className="hidden md:inline">Copy</span>
                            </span>

                            <span className="hidden peer-checked:flex items-center space-x-2">
                              <LuCopyCheck />

                              <span className="hidden md:inline">Copied</span>
                            </span>
                          </label>
                        </span>
                      </div>
                    </div>

                    <div className="w-full pt-0 py-2 px-3">
                      <SyntaxHighlighter language="javascript" style={a11yDark}>
                        {`import React, { useEffect } from "react";

const SampleEmbeddedForm = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://appscript-scripts.s3.ap-south-1.amazonaws.com/boloforms-embed-index.min.js";
    script.async = true;
    script.onload = () => {
      if (document.querySelector("#embed-container").childElementCount > 0)
        return;

      // Create an instance of BoloFormsEmbed once the script is loaded
      const embed = new BoloFormsEmbed({
        containerId: "embed-container",
        token: "your-embed-key",
        iframeOpts: { width: "100%", height: "600px" },
        sessionInfo: { userId: 1, userName: "John Doe" },
      });
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return <div id="embed-container" />;
};`}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* <section className="w-full mt-10 sm:mt-28 flex sm:justify-center sm:items-center flex-col">
            <div className="w-full mx-auto lg:mx-0 max-w-screen-lg text-left sm:text-center">
              <p className="uppercase text-sm sm:text-lg font-semibold mb-2 sm:mb-3">
                More Embedding
              </p>

              <h2 className="text-3xl lg:text-5xl lg:leading-tight text-gray-900 font-bold mb-4">
                Embedded drag &amp; drop document form builder
              </h2>

              <p className="text-lg sm:text-2xl lg:leading-8 text-gray-700 px-4">
                Not only can you seamlessly integrate forms for document
                signing, but you also have the capability to embed the entire
                interface for creating these documents. Say goodbye to intricate
                integration processes. By simply incorporating a few lines of
                code, you can effortlessly introduce a comprehensive document
                builder and additional features, enhancing the user experience
                on your platform.
              </p>
            </div>

            <div className="flex justify-center lg:justify-start my-8">
              <div className="flex items-center gap-3 flex-wrap">
                <CLMButtonPrimary
                  buttonText={"Try now for free"}
                  buttonUrl={
                    "https://signature.boloforms.com/settings?type=DEV_SEC&subType=EMBED&from=EMBEDDING_PAGE"
                  }
                  icon={<HiArrowRight />}
                />
                <CLMButtonSecondaryBorder
                  buttonText="Documentation"
                  buttonUrl="https://developer.boloforms.com/"
                  icon={<MdLibraryBooks />}
                />
              </div>
            </div>
          </section> */}
        </div>
      </div>
      {/* <div className="mt-12 md:mt-24">
        <Subscribe />
      </div> */}
    </>
  );
};

export default Embedding;
